// White left sidebar for the Marketing workspace (portal style).
// Nav groups: Run (do the work) / Measure (read the results) / Tools
// (transitional utilities that keep working until their redesigned
// replacements land in later phases).

import { NavLink } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

const RUN = [
  { to: '/marketing', label: 'Dashboard', icon: '▣', end: true },
  { to: '/marketing/content', label: 'Content', icon: '✎' },
  { to: '/marketing/social', label: 'Social', icon: '☼' },
  { to: '/marketing/projects', label: 'Projects', icon: '□' },
  { to: '/marketing/planning', label: 'Planning', icon: '◎' },
];

const MEASURE = [
  { to: '/marketing/attribution', label: 'Attribution', icon: '◳' },
  { to: '/marketing/email', label: 'Email', icon: '✉' },
  { to: '/marketing/seo', label: 'SEO / Search', icon: '↗' },
  { to: '/marketing/budget', label: 'Budget & CAC', icon: '$' },
];

// Remaining old tools. The Content Log folds into Content once its full
// edit modal moves over; the UTM builder stays a tool by design.
const TOOLS = [
  { to: '/marketing/content-log', label: 'Content Log' },
  { to: '/marketing/utm', label: 'UTM Builder' },
];

function NavItem({ to, label, icon, end, soft }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 px-3 py-2 rounded-[7px] text-[13px] transition-colors ${
          isActive
            ? 'bg-bullogic-blue/10 text-navy font-bold'
            : soft
              ? 'text-ink-2 font-medium hover:bg-[#f6f4ef]'
              : 'text-ink font-semibold hover:bg-[#f6f4ef]'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute -left-3 top-[7px] bottom-[7px] w-[3px] rounded-r-[3px] bg-bullogic-blue" />
          )}
          <span className={`w-[18px] text-center text-[13px] ${isActive ? 'text-bullogic-blue' : 'text-ink-3'}`}>
            {icon || '·'}
          </span>
          {label}
        </>
      )}
    </NavLink>
  );
}

function GroupLabel({ children }) {
  return (
    <div className="text-[10px] font-black tracking-[0.15em] uppercase text-ink-3 px-3 pt-4 pb-1.5">
      {children}
    </div>
  );
}

// Bull mark from the mockup — navy rounded square, horns, nose dot.
function BullMark() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      <rect width="30" height="30" rx="7" fill="#022889" />
      <path d="M8 10c2 4 4 5 7 5s5-1 7-5" stroke="#3EAAD0" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <path d="M8 10c-1-2-2-2.6-3-2.6M22 10c1-2 2-2.6 3-2.6" stroke="#2596BE" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <circle cx="15" cy="19" r="2.1" fill="#fff" />
    </svg>
  );
}

// Derive display bits from the signed-in email (AuthContext only exposes
// { id, email }). adam@bullogicwealth.com -> "Adam" / "AB".
function displayName(email) {
  const local = (email || '').split('@')[0] || 'there';
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function initials(email) {
  const [local = '', domain = ''] = (email || '').split('@');
  return ((local[0] || 'B') + (domain[0] || 'W')).toUpperCase();
}

export default function MarketingSidebar() {
  const { user } = useAuth();

  return (
    <aside className="w-[230px] shrink-0 bg-white border border-line rounded-[8px] shadow-[0_1px_3px_rgba(2,10,40,0.06)] px-3 py-4 flex flex-col self-start sticky top-4 max-h-[calc(100vh-32px)] overflow-y-auto">
      <div className="flex items-center gap-2.5 px-2 pb-4 border-b border-line-2 mb-2">
        <BullMark />
        <div>
          <div className="text-navy font-black text-[16px] leading-none">Bullogic</div>
          <div className="text-deep-teal text-[10px] font-black tracking-[0.16em] uppercase mt-[3px]">Marketing</div>
        </div>
      </div>

      <nav className="flex flex-col gap-0.5">
        <GroupLabel>Run</GroupLabel>
        {RUN.map(item => <NavItem key={item.to} {...item} />)}

        <GroupLabel>Measure</GroupLabel>
        {MEASURE.map(item => <NavItem key={item.to} {...item} />)}

        <GroupLabel>Tools</GroupLabel>
        {TOOLS.map(item => <NavItem key={item.to} {...item} soft />)}
      </nav>

      <div className="mt-auto pt-4">
        <div className="bg-[#f7f5f0] border border-line-2 rounded-[9px] p-2.5 flex items-center gap-2.5">
          <div className="w-[34px] h-[34px] rounded-full bg-deep-teal text-white grid place-items-center font-black text-[12px] shrink-0">
            {initials(user?.email)}
          </div>
          <div>
            <div className="text-navy font-black text-[12.5px]">{displayName(user?.email)}</div>
            <div className="text-ink-2 text-[11px]">Marketing lead</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
